import { clsx } from 'clsx'
import styles from './OptionCard.module.css'

type OptionCardProps = {
  id: string
  value: string
  title: string
  description?: string
  /** Small pill shown top-left (e.g. "PADRÃO") — standard layout only */
  badge?: string
  /** Emoji shown in a 36px icon box — activates icon card layout */
  icon?: string
  /** Eyebrow label shown above the title — icon card layout only */
  eyebrow?: string
  isSelected: boolean
  isLocked?: boolean
  /** Makes the card span both columns in a 2-col grid */
  fullWidth?: boolean
  onChange?: (value: string) => void
}

export function OptionCard({
  id,
  value,
  title,
  description,
  badge,
  icon,
  eyebrow,
  isSelected,
  isLocked = false,
  fullWidth = false,
  onChange,
}: OptionCardProps) {
  const isIconVariant = !!icon

  if (isLocked) {
    return (
      <div
        id={id}
        role="radio"
        aria-checked={false}
        aria-disabled={true}
        tabIndex={0}
        className={clsx(
          styles.card,
          isIconVariant && styles.iconCard,
          styles.isLocked,
          fullWidth && styles.fullWidth,
        )}
      >
        {isIconVariant ? (
          <>
            <div className={styles.iconBox}>{icon}</div>
            {eyebrow && <span className={styles.cardEyebrow}>{eyebrow}</span>}
            <span className={styles.iconCardTitle}>{title}</span>
          </>
        ) : (
          <>
            <div className={styles.topRow}>
              <span />
              <span className={styles.lockBadge}>EM BREVE</span>
            </div>
            <span className={styles.cardTitle}>{title}</span>
            {description && <span className={styles.cardDesc}>{description}</span>}
          </>
        )}
      </div>
    )
  }

  return (
    <div
      id={id}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      className={clsx(
        styles.card,
        isIconVariant && styles.iconCard,
        isSelected && styles.isSelected,
        fullWidth && styles.fullWidth,
      )}
      onClick={() => onChange?.(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onChange?.(value)
        }
      }}
    >
      {isIconVariant ? (
        <>
          <div
            className={clsx(styles.checkBadge, isSelected && styles.checkBadgeVisible)}
            aria-hidden="true"
          >
            ✓
          </div>
          <div className={styles.iconBox}>{icon}</div>
          {eyebrow && <span className={styles.cardEyebrow}>{eyebrow}</span>}
          <span className={styles.iconCardTitle}>{title}</span>
          {description && <span className={styles.cardDesc}>{description}</span>}
        </>
      ) : (
        <>
          <div className={styles.topRow}>
            {badge ? <span className={styles.badge}>{badge}</span> : <span />}
            <span
              className={clsx(styles.indicator, isSelected && styles.indicatorSelected)}
              aria-hidden="true"
            />
          </div>
          <span className={styles.cardTitle}>{title}</span>
          {description && <span className={styles.cardDesc}>{description}</span>}
        </>
      )}
    </div>
  )
}
